import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Home", href: "#top" },
  { label: "Subjects", href: "#subjects" },
  { label: "Why Us", href: "#why" },
  { label: "Journey", href: "#journey" },
  { label: "Faculty", href: "#faculty" },
  { label: "Voices", href: "#testimonials" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  const scrollToHash = (hash, behavior = "smooth") => {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    const id = hash?.startsWith("#") ? hash.slice(1) : "";
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top, behavior });
  };

  const handleNavClick = (event, href) => {
    if (!href || !href.startsWith("#")) return;
    event.preventDefault();
    setOpen(false);
    if (typeof window !== "undefined") {
      window.location.hash = href;
      window.requestAnimationFrame(() => scrollToHash(href, "smooth"));
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onHashChange = () => scrollToHash(window.location.hash, "smooth");
    const onLoad = () => scrollToHash(window.location.hash, "auto");
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("load", onLoad);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("load", onLoad);
    };
  }, []);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid="site-navbar"
      className="site-navbar fixed top-0 left-0 right-0 z-[100] bg-[#FDFBF7]/70 backdrop-blur-2xl border-b border-white/40 shadow-[0_10px_30px_rgba(11,19,43,0.08)]"
    >
      <div className="site-navbar__inner px-4 sm:px-6 md:px-8 lg:px-16 py-4 grid grid-cols-[auto_1fr_auto] md:grid-cols-[1fr_auto_1fr] items-center gap-3">
        <a
          href="#top"
          data-testid="logo-link"
          className="site-navbar__brand flex items-center gap-2"
          onClick={(event) => handleNavClick(event, "#top")}
        >
          <span className="site-navbar__logo w-9 h-9 sm:w-10 sm:h-10 rounded-[2rem] bg-[#0B132B] grid place-items-center shadow-[0_6px_14px_rgba(11,19,43,0.22)]">
            <img
              src="/images/nova-logo.png"
              alt="LearnWithNova logo"
              className="w-8 h-8 sm:w-9 sm:h-9 object-contain drop-shadow-[0_2px_6px_rgba(255,255,255,0.35)]"
              loading="eager"
            />
          </span>
          <span className="site-navbar__title font-display text-lg sm:text-xl font-extrabold tracking-tight text-[#0B132B]">
            LearnWith<span className="text-[#FF6B6B]">Nova</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center justify-center gap-6 lg:gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="link-underline text-sm font-medium text-[#1C2541]"
              onClick={(event) => handleNavClick(event, l.href)}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen((v) => !v)}
          className="site-navbar__menu-btn md:hidden w-9 h-9 sm:w-10 sm:h-10 grid place-items-center justify-self-end rounded-full bg-[#0B132B] text-[#FDFBF7]"
          aria-label="Menu"
          aria-expanded={open}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="site-navbar__menu-panel md:hidden overflow-hidden bg-[#FDFBF7]/80 backdrop-blur-2xl border-t border-white/40"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(event) => handleNavClick(event, l.href)}
                  className="text-base font-medium text-[#1C2541]"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
